import React, { useCallback } from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Modal,
  useModal,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type {
  ResubscriptionModalData,
  SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'

import { CtaButton } from '../ResubscribeConfirmation/ConfirmButtons'
import type { Theme } from '../ResubscribeConfirmation/theme'
import { FormattedText } from '../helpers/FormattedText'
import {
  RESUBSCRIPTION_MODAL_INTENTS,
  useResubscriptionModalIntentHandler,
} from '../hooks/useResubscriptionModalIntentHandler'

export const RESUBSCRIPTION_CONFIRMATION_MODAL_NAME = 'RESUBSCRIPTION-CONFIRMATION-MODAL'

const HEADLINE_ID = 'resubscription-modal-headline'

// Scrolls inside the card, not the modal wrapper, so the wrapper never clips the card's shadow.
const Card = withCitrusTheme(Box, () => ({
  borderRadius: '16px',
  minHeight: 0,
  overflowY: 'auto',
}))

const Badge = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
}))

const Headline = withCitrusTheme(Heading3, (theme: Theme) => theme.responsiveFont([7, 7, 7, 7]))

const Message = withCitrusTheme(Body2, (theme: Theme) => theme.responsiveFont([4, 4, 4, 4]))

const BadgeText = withCitrusTheme(Body3, (theme: Theme) => theme.responsiveFont([2, 2, 2, 2]))

const CloseButton = withCitrusTheme(Button, (theme: Theme) => ({
  color: theme.tokens.colors.ColdGrey_800,
  '&:hover, &:focus, &:active': { color: theme.tokens.colors.ColdGrey_800 },
  borderRadius: '8px',
  minWidth: '48px',
  minHeight: '48px',
}))

const PrimaryCta = withCitrusTheme(CtaButton, (theme: Theme) => [
  theme.responsiveFont([6, 6, 6, 6]),
  { height: '48px', paddingTop: 0, paddingBottom: 0 },
])

const TextCta = withCitrusTheme(Button, (theme: Theme) => [
  theme.responsiveFont([6, 6, 6, 6]),
  { borderRadius: '8px', height: '28px', paddingTop: 0, paddingBottom: 0 },
])

type ResubscriptionConfirmationModalProps = {
  data: ResubscriptionModalData
  isOpen?: boolean
  onClosed?: () => void
  onDismissed?: () => void
}

export const ResubscriptionConfirmationModal = ({
  data,
  isOpen,
  onClosed,
  onDismissed,
}: ResubscriptionConfirmationModalProps) => {
  const { closeCurrentModal } = useModal()
  const close = useCallback(() => {
    closeCurrentModal()
    onClosed?.()
  }, [closeCurrentModal, onClosed])
  const { onAction } = useResubscriptionModalIntentHandler(close)
  const { headline, badge, message, primaryAction, secondaryAction, dismissAnalytics } = data

  const act = (intent: string, analytics: SubscriptionSettingsAnalytics | undefined) => {
    onAction(intent, analytics)
    if (intent === RESUBSCRIPTION_MODAL_INTENTS.dismiss) {
      onDismissed?.()
    }
  }
  const dismiss = () => act(RESUBSCRIPTION_MODAL_INTENTS.dismiss, dismissAnalytics)

  return (
    <Modal
      name={RESUBSCRIPTION_CONFIRMATION_MODAL_NAME}
      isOpen={isOpen}
      aria={{ labelledby: HEADLINE_ID }}
      shouldCloseOnOverlayClick={false}
      contentElement={(props, children) => (
        <div
          {...props}
          style={{
            ...props.style,
            background: 'transparent',
            borderRadius: '16px',
            overflow: 'visible',
          }}
        >
          {children}
        </div>
      )}
      onRequestClose={dismiss}
    >
      <Card
        bg={Color.White}
        paddingH={4}
        paddingV={4}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={[2, 0, 0, 0]}
        style={{ boxShadow: '0 4px 6px -2px #21293008, 0 12px 16px -4px #2129300F' }}
      >
        <Box
          display={Display.Flex}
          alignItems={AlignItems.FlexStart}
          justifyContent={JustifyContent.SpaceBetween}
          gap={2}
        >
          <Box style={{ minWidth: 0, overflowWrap: 'anywhere', paddingTop: '8px' }}>
            <Headline id={HEADLINE_ID} color={Color.ColdGrey_800}>
              {headline}
            </Headline>
          </Box>
          <CloseButton
            onClick={dismiss}
            colorVariant={ButtonColorVariant.Tertiary}
            variant={ButtonVariant.Icon}
            icon="close"
          >
            Close modal
          </CloseButton>
        </Box>

        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
          {badge && (
            <Badge
              data-testid="resubscription-modal-badge"
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              gap={1}
              bg={Color.Success_50}
              paddingH={2}
              paddingV={0.5}
            >
              <Box display={Display.Flex} color={Color.Success_900}>
                <Icon name="offer" size={4} variant={IconVariant.Inherit} />
              </Box>
              <BadgeText color={Color.Success_900}>
                <FormattedText text={badge.label} />
              </BadgeText>
            </Badge>
          )}
          <Message color={Color.ColdGrey_800}>
            <FormattedText text={message} />
          </Message>
        </Box>

        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={4}
          style={{ paddingTop: '16px' }}
        >
          <PrimaryCta
            width="100%"
            colorVariant={ButtonColorVariant.Primary}
            onClick={() => act(primaryAction.intent, primaryAction.analytics)}
          >
            {primaryAction.title}
          </PrimaryCta>
          {secondaryAction && (
            <TextCta
              width="100%"
              colorVariant={ButtonColorVariant.Tertiary}
              onClick={() => act(secondaryAction.intent, secondaryAction.analytics)}
            >
              {secondaryAction.title}
            </TextCta>
          )}
        </Box>
      </Card>
    </Modal>
  )
}
